/**
 * Bank Account Dialog
 * 
 * Dialog สำหรับเพิ่ม/แก้ไขบัญชีธนาคาร
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Building2,
  Settings,
  Shield,
  Loader2,
  AlertTriangle,
  Save,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BankAccount, BankAccountFormData, BankAccountType, BankAccountStatus, BankListItem } from '@/domain/bank-account'

interface BankAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  account?: BankAccount | null  // null = create mode, BankAccount = edit mode
  bankList: BankListItem[]
  onSave: (data: BankAccountFormData) => Promise<boolean>
}

const defaultFormData: BankAccountFormData = {
  name: '',
  bank_account: '',
  banklist_id: '',
  bank_name: '',
  image_url: '',
  bank_id: '',
  bank_tag: '',
  type: 'Deposit',
  status: 'Active',
  amount: 0,
  api_status: 'suspend',
  primaryDeposit: false,
  primaryWithdraw: false,
  bankMaintenance: false,
  maintenanceMessage: '',
  bankApi: false,
  slipApi: false,
  lineconnectApi: false,
  gatewayApi: false,
  show_transfer: false,
  access_key_key: '',
  access_key_bank_id: '',
}

export function BankAccountDialog({
  open,
  onOpenChange,
  account,
  bankList,
  onSave,
}: BankAccountDialogProps) {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [formData, setFormData] = useState<BankAccountFormData>(defaultFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  
  const isEditMode = !!account
  
  // Initialize form data when dialog opens
  useEffect(() => {
    if (open) {
      if (account) {
        // Edit mode - populate form with existing data
        setFormData({
          name: account.name || '',
          bank_account: account.bank_account || '',
          banklist_id: '', // We'll need to match this
          bank_name: account.bank_name || '',
          image_url: account.image_url || '',
          bank_id: account.bank_id || '',
          bank_tag: account.bank_tag || '',
          type: account.type || 'Deposit',
          status: account.status || 'Active',
          amount: account.amount || 0,
          api_status: account.api_status || 'suspend',
          primaryDeposit: account.primaryDeposit || false,
          primaryWithdraw: account.primaryWithdraw || false,
          bankMaintenance: account.bankMaintenance || false,
          maintenanceMessage: account.maintenanceMessage || '',
          bankApi: account.bankApi || false,
          slipApi: account.slipApi || false,
          lineconnectApi: account.lineconnectApi || false,
          gatewayApi: account.gatewayApi || false,
          show_transfer: account.show_transfer || false,
          access_key_key: account.access_key?.key || '',
          access_key_bank_id: account.access_key?.bank_id || '',
        })
      } else {
        // Create mode - reset form
        setFormData(defaultFormData)
      }
      setErrors({})
      setActiveTab('basic')
    }
  }, [open, account])
  
  // Update form field
  const updateField = <K extends keyof BankAccountFormData>(
    field: K,
    value: BankAccountFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }
  
  // Handle bank selection - also update bank_name, image_url, bank_id
  const handleBankSelect = (banklistId: string) => {
    const selectedBank = bankList.find(b => b.id === banklistId)
    if (selectedBank) {
      // ✅ ตรวจสอบว่าเป็น TrueWallet หรือไม่ (id = '19' หรือ bank_id = 'truewallet')
      const isTrueWallet = selectedBank.id === '19' || selectedBank.bank_id === 'truewallet'

      setFormData(prev => ({
        ...prev,
        banklist_id: banklistId,
        bank_name: selectedBank.bank_name,
        image_url: selectedBank.image_url,
        bank_id: selectedBank.bank_id,
        // ✅ ถ้าเป็น TrueWallet → บังคับให้เป็น Deposit อย่างเดียว
        type: isTrueWallet ? 'Deposit' : prev.type,
      }))
      // Clear error
      if (errors.banklist_id) {
        setErrors(prev => ({ ...prev, banklist_id: '' }))
      }
    }
  }
  
  // Get selected bank info
  const selectedBank = bankList.find(b => b.id === formData.banklist_id)

  // ✅ ตรวจสอบว่าเป็น TrueWallet หรือไม่
  const isTrueWallet = selectedBank?.id === '19' || selectedBank?.bank_id === 'truewallet' || formData.bank_id === 'truewallet'
  
  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'กรุณากรอกชื่อบัญชี'
    }
    if (!formData.bank_account.trim()) {
      newErrors.bank_account = 'กรุณากรอกเลขบัญชี'
    }
    if (!formData.banklist_id && !isEditMode) {
      newErrors.banklist_id = 'กรุณาเลือกธนาคาร'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }
  
  // Show confirmation before save
  const handleSaveClick = () => {
    if (!validateForm()) {
      setActiveTab('basic') // Switch to basic tab to show errors
      return
    }
    setShowConfirmSave(true)
  }
  
  // Handle actual save
  const handleConfirmSave = async () => {
    setShowConfirmSave(false)
    setIsSaving(true)
    try {
      const success = await onSave(formData)
      if (success) {
        onOpenChange(false)
      }
    } finally {
      setIsSaving(false)
    }
  }
  
  // Get selected bank info (used for potential future enhancement)
  const _selectedBank = bankList.find(b => b.id === formData.banklist_id)
  void _selectedBank // Suppress unused variable warning
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isEditMode ? t('banks.editAccount') : t('banks.addAccount')}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'แก้ไขข้อมูลบัญชีธนาคาร'
              : 'เพิ่มบัญชีธนาคารใหม่สำหรับรับฝาก-ถอน'
            }
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic" className="gap-2">
              <Building2 className="h-4 w-4" />
              ข้อมูลพื้นฐาน
            </TabsTrigger>
            <TabsTrigger value="api" className="gap-2">
              <Settings className="h-4 w-4" />
              ตั้งค่า API
            </TabsTrigger>
            <TabsTrigger value="advanced" className="gap-2">
              <Shield className="h-4 w-4" />
              ขั้นสูง
            </TabsTrigger>
          </TabsList>
          
          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            {/* Bank Selection with Preview */}
            <div className="space-y-2">
              <Label>{t('banks.selectBank')} *</Label>
              <div className="flex items-center gap-3">
                {/* Bank Logo Preview */}
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center border overflow-hidden flex-shrink-0">
                  {(selectedBank?.image_url || formData.image_url) ? (
                    <img 
                      src={selectedBank?.image_url || formData.image_url} 
                      alt={selectedBank?.bank_name || formData.bank_name} 
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <Building2 className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                
                {/* Bank Dropdown */}
                <div className="flex-1">
                  <Select
                    value={formData.banklist_id}
                    onValueChange={handleBankSelect}
                    disabled={isEditMode}
                  >
                    <SelectTrigger className={cn(errors.banklist_id && 'border-red-500')}>
                      <SelectValue placeholder="เลือกธนาคาร">
                        {selectedBank?.bank_name || formData.bank_name || 'เลือกธนาคาร'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {bankList.map(bank => (
                        <SelectItem key={bank.id} value={bank.id}>
                          <div className="flex items-center gap-2">
                            {bank.image_url && (
                              <img src={bank.image_url} alt={bank.bank_name} className="w-5 h-5 rounded object-contain" />
                            )}
                            {bank.bank_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.banklist_id && (
                    <p className="text-sm text-red-500 mt-1">{errors.banklist_id}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Account Number & Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('banks.accountNumber')} *</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => updateField('bank_account', e.target.value)}
                  placeholder="xxx-x-xxxxx-x"
                  className={cn(errors.bank_account && 'border-red-500')}
                />
                {errors.bank_account && (
                  <p className="text-sm text-red-500">{errors.bank_account}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>{t('banks.accountHolder')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="ชื่อเจ้าของบัญชี"
                  className={cn(errors.name && 'border-red-500')}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>
            </div>
            
            {/* Bank Tag */}
            <div className="space-y-2">
              <Label>{t('banks.bankTag')}</Label>
              <Input
                value={formData.bank_tag}
                onChange={(e) => updateField('bank_tag', e.target.value)}
                placeholder={t('banks.bankTagHint')}
              />
              <p className="text-xs text-muted-foreground">
                ชื่อเรียกย่อสำหรับระบุธนาคาร เช่น KTB1, SCB1
              </p>
            </div>
            
            {/* Type & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ประเภท</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => updateField('type', v as BankAccountType)}
                  disabled={isTrueWallet} // ✅ TrueWallet ต้องเป็น Deposit อย่างเดียว
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Deposit">
                      <Badge variant="success" className="mr-2">ฝาก</Badge>
                      สำหรับรับเงินฝาก
                    </SelectItem>
                    <SelectItem value="Withdraw" disabled={isTrueWallet}>
                      <Badge variant="warning" className="mr-2">ถอน</Badge>
                      สำหรับถอนเงิน
                    </SelectItem>
                    <SelectItem value="Both" disabled={isTrueWallet}>
                      <Badge variant="default" className="mr-2">ฝาก-ถอน</Badge>
                      ใช้ได้ทั้งสองอย่าง
                    </SelectItem>
                  </SelectContent>
                </Select>
                {/* ✅ แสดงข้อความอธิบายสำหรับ TrueWallet */}
                {isTrueWallet && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-orange-600 dark:bg-orange-400"></span>
                    TrueWallet สามารถเป็นบัญชีฝากได้เท่านั้น
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>สถานะ</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => updateField('status', v as BankAccountStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">ใช้งาน</SelectItem>
                    <SelectItem value="Inactive">ปิดใช้งาน</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Balance */}
            <div className="space-y-2">
              <Label>{t('banks.balance')}</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
          </TabsContent>
          
          {/* API Config Tab */}
          <TabsContent value="api" className="space-y-4 mt-4">
            {/* API Status */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div>
                <p className="font-medium">{t('banks.apiStatus')}</p>
                <p className="text-sm text-muted-foreground">
                  {formData.api_status === 'active' ? 'API กำลังทำงาน' : 'API หยุดทำงาน'}
                </p>
              </div>
              <Switch
                checked={formData.api_status === 'active'}
                onCheckedChange={(checked) => 
                  updateField('api_status', checked ? 'active' : 'suspend')
                }
              />
            </div>
            
            {/* API Toggles */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label className="font-normal">{t('banks.bankApi')}</Label>
                <Switch
                  checked={formData.bankApi}
                  onCheckedChange={(checked) => updateField('bankApi', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label className="font-normal">{t('banks.slipApi')}</Label>
                <Switch
                  checked={formData.slipApi}
                  onCheckedChange={(checked) => updateField('slipApi', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label className="font-normal">{t('banks.lineApi')}</Label>
                <Switch
                  checked={formData.lineconnectApi}
                  onCheckedChange={(checked) => updateField('lineconnectApi', checked)}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label className="font-normal">{t('banks.gatewayApi')}</Label>
                <Switch
                  checked={formData.gatewayApi}
                  onCheckedChange={(checked) => updateField('gatewayApi', checked)}
                />
              </div>
            </div>
            
            {/* Access Key */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium">Access Key</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('banks.accessKey')}</Label>
                  <Input
                    value={formData.access_key_key}
                    onChange={(e) => updateField('access_key_key', e.target.value)}
                    placeholder="รหัส Access Key"
                    type="password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('banks.accessKeyBankId')}</Label>
                  <Input
                    value={formData.access_key_bank_id}
                    onChange={(e) => updateField('access_key_bank_id', e.target.value)}
                    placeholder="Bank ID สำหรับ API"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
          
          {/* Advanced Settings Tab */}
          <TabsContent value="advanced" className="space-y-4 mt-4">
            {/* Primary Account Toggles */}
            <div className="space-y-3">
              <h4 className="font-medium">บัญชีหลัก</h4>
              {(formData.type === 'Deposit' || formData.type === 'Both') && (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-emerald-500/5 border-emerald-500/30">
                  <div>
                    <Label className="font-normal">{t('banks.primaryDeposit')}</Label>
                    <p className="text-xs text-muted-foreground">ใช้เป็นบัญชีฝากหลัก</p>
                  </div>
                  <Switch
                    checked={formData.primaryDeposit}
                    onCheckedChange={(checked) => updateField('primaryDeposit', checked)}
                  />
                </div>
              )}
              {(formData.type === 'Withdraw' || formData.type === 'Both') && (
                <div className="flex items-center justify-between p-3 rounded-lg border bg-rose-500/5 border-rose-500/30">
                  <div>
                    <Label className="font-normal">{t('banks.primaryWithdraw')}</Label>
                    <p className="text-xs text-muted-foreground">
                      {isTrueWallet ? 'TrueWallet ไม่สามารถเป็นบัญชีถอนหลักได้' : 'ใช้เป็นบัญชีถอนหลัก'}
                    </p>
                  </div>
                  <Switch
                    checked={formData.primaryWithdraw}
                    onCheckedChange={(checked) => updateField('primaryWithdraw', checked)}
                    disabled={isTrueWallet} // ✅ TrueWallet ไม่สามารถเป็นบัญชีถอนหลักได้
                  />
                </div>
              )}
            </div>
            
            {/* Maintenance Mode */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                {t('banks.maintenanceMode')}
              </h4>
              <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-500/5 border-amber-500/30">
                <div>
                  <Label className="font-normal">เปิดโหมดปรับปรุง</Label>
                  <p className="text-xs text-muted-foreground">ปิดการใช้งานชั่วคราว</p>
                </div>
                <Switch
                  checked={formData.bankMaintenance}
                  onCheckedChange={(checked) => updateField('bankMaintenance', checked)}
                />
              </div>
              
              {formData.bankMaintenance && (
                <div className="space-y-2">
                  <Label>{t('banks.maintenanceMessage')}</Label>
                  <Textarea
                    value={formData.maintenanceMessage}
                    onChange={(e) => updateField('maintenanceMessage', e.target.value)}
                    placeholder="ข้อความแจ้งเตือนเมื่อธนาคารปิดปรับปรุง..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            {/* Show Transfer Button */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label className="font-normal">{t('banks.showTransfer')}</Label>
                <p className="text-xs text-muted-foreground">แสดงปุ่มโอนเงินในรายการ</p>
              </div>
              <Switch
                checked={formData.show_transfer}
                onCheckedChange={(checked) => updateField('show_transfer', checked)}
              />
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            ยกเลิก
          </Button>
          <Button onClick={handleSaveClick} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            {isEditMode ? 'บันทึก' : 'เพิ่มบัญชี'}
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Confirm Save Dialog */}
      <AlertDialog open={showConfirmSave} onOpenChange={setShowConfirmSave}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isEditMode ? 'ยืนยันการบันทึก' : 'ยืนยันการเพิ่มบัญชี'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isEditMode 
                ? `ต้องการบันทึกการเปลี่ยนแปลงบัญชี "${formData.name}" หรือไม่?`
                : `ต้องการเพิ่มบัญชี "${formData.name}" หรือไม่?`
              }
              {formData.status === 'Inactive' && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ สถานะบัญชีถูกตั้งเป็น "ปิดใช้งาน"
                </span>
              )}
              {formData.api_status === 'suspend' && (
                <span className="block mt-1 text-amber-600">
                  ⚠️ API ถูกตั้งเป็น "ระงับการใช้งาน"
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>
              {isEditMode ? 'บันทึก' : 'เพิ่มบัญชี'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}





